import { Application, Container } from 'pixi.js';
import { SceneStage } from '../scene/SceneStage.js';
import { ConsolePanel, type ConsoleCommand } from '../ui/ConsolePanel.js';
import { animationForScene, detectLocale, type Locale } from '../story/caseModel.js';
import {
  actionDialogue,
  consultation,
  sceneDialogue,
  type CastLine,
  type CastMember,
} from '../story/castDialogue.js';
import type { StorySnapshot, StorySource } from '../story/source.js';

const REPLAY_ID = '__replay';
const UI: Record<Locale, { replay: string; replayHint: string; booting: string; error: string }> = {
  'fr-FR': {
    replay: '/rejouer',
    replayHint: "Relancer l'affaire",
    booting:
      'Console en ligne. Patch et Bit comptent sur toi. Touche un personnage pour lui parler.',
    error:
      'Connexion interrompue. Les choix sont suspendus : recharge pour retrouver la progression enregistrée.',
  },
  'en-US': {
    replay: '/replay',
    replayHint: 'Run the case again',
    booting: 'Console online. Patch and Bit are counting on you. Tap a character to talk.',
    error: 'Connection interrupted. Choices are paused: reload to retrieve saved progress.',
  },
  'zh-CN': {
    replay: '/重玩',
    replayHint: '重新调查本案',
    booting: '控制台已上线。Patch 和 Bit 正在等你。点击角色即可交谈。',
    error: '连接中断，选项已暂停。请重新加载以恢复已保存的进度。',
  },
};

/** Presentation only: the story source remains responsible for clues and outcomes. */
export class StoryScene {
  readonly view = new Container();
  private readonly locale = detectLocale();
  private source!: StorySource;
  private snapshot!: StorySnapshot;
  private stage!: SceneStage;
  private terminal!: ConsolePanel;
  private busy = true;
  private disposed = false;

  static async create(app: Application, source: StorySource): Promise<StoryScene> {
    const scene = new StoryScene();
    await scene.build(app, source);
    return scene;
  }

  private async build(app: Application, source: StorySource): Promise<void> {
    this.source = source;
    const { width, height } = app.screen;
    const stageHeight = Math.round(height * 0.64);
    [this.stage, this.snapshot] = await Promise.all([
      SceneStage.create(width, stageHeight),
      source.start(),
    ]);
    this.view.addChild(this.stage.view);
    this.terminal = new ConsolePanel(width, height - stageHeight);
    this.terminal.view.y = stageHeight;
    this.terminal.setOnSelect((id) => {
      void this.onSelect(id);
    });
    this.stage.setOnConsult((speaker) => {
      void this.consult(speaker);
    });
    this.view.addChild(this.terminal.view);
    this.terminal.append(UI[this.locale].booting, 'system', true);
    this.lock();
    // Do not await player input while creating the scene: the ticker needs to start.
    void this.presentScene(true).then(() => {
      this.unlock();
    });
  }

  private isDisposed(): boolean {
    return this.disposed;
  }

  private lock(): void {
    this.busy = true;
    this.terminal.setCommands([]);
    this.stage.setConsultEnabled(false);
    this.stage.pauseCountdown();
  }

  private unlock(): void {
    if (this.isDisposed()) return;
    this.busy = false;
    this.stage.setConsultEnabled(true);
    if (!this.snapshot.isEnding) this.stage.startCountdown();
    const copy = UI[this.locale];
    const commands: ConsoleCommand[] = this.snapshot.isEnding
      ? [{ id: REPLAY_ID, text: copy.replay, hint: copy.replayHint }]
      : this.snapshot.choices.map((choice) => {
          const command = choice.command?.[this.locale];
          const label = choice.label[this.locale];
          return command === undefined
            ? { id: choice.id, text: label }
            : { id: choice.id, text: command, hint: label };
        });
    this.terminal.setCommands(commands);
  }

  private async presentScene(narrate: boolean): Promise<void> {
    if (this.isDisposed()) return;
    const { scene, evidence } = this.snapshot;
    this.stage.setMood(animationForScene(scene));
    this.stage.setEvidence(evidence.count, evidence.total);
    if (!narrate) return;
    this.terminal.append('== ' + scene.title[this.locale].toUpperCase() + ' ==', 'system', true);
    // The original briefing/report stays available in the scrollable journal.
    this.terminal.append(scene.body[this.locale], 'narration', true);
    await this.playSpeech(sceneDialogue(this.snapshot, this.locale));
  }

  private async playSpeech(lines: readonly CastLine[]): Promise<void> {
    for (const line of lines) {
      if (this.isDisposed()) return;
      await this.stage.speak(line.speaker, line.text, line.gesture);
      if (this.isDisposed()) return;
      this.terminal.append(line.speaker.toUpperCase() + ' — ' + line.text, 'narration', true);
    }
    if (!this.isDisposed()) this.stage.clearSpeech();
  }

  private async consult(speaker: CastMember): Promise<void> {
    if (this.busy || this.isDisposed()) return;
    this.lock();
    await this.playSpeech([consultation(this.snapshot, speaker, this.locale)]);
    this.unlock();
  }

  private async onSelect(id: string): Promise<void> {
    if (this.busy || this.isDisposed()) return;
    if (id !== REPLAY_ID && !this.snapshot.choices.some((choice) => choice.id === id)) return;
    this.lock();
    try {
      if (id === REPLAY_ID) {
        const snapshot = await this.source.restart();
        if (this.isDisposed()) return;
        this.snapshot = snapshot;
        this.stage.resetCountdown();
        this.terminal.clearLog();
        this.terminal.append(UI[this.locale].booting, 'system', true);
        await this.presentScene(true);
      } else {
        await this.runChoiceBeat(id);
      }
      this.unlock();
    } catch (error) {
      if (this.isDisposed()) return;
      console.error('[mini-app] choice failed', error);
      // A failed request may already have committed server-side. Never retry
      // a potentially stale choice implicitly or claim that it was rolled back.
      this.terminal.append(UI[this.locale].error, 'bad', true);
    }
  }

  private async runChoiceBeat(id: string): Promise<void> {
    const choice = this.snapshot.choices.find((candidate) => candidate.id === id);
    if (choice === undefined) return;
    this.terminal.append(
      choice.command?.[this.locale] ?? choice.label[this.locale],
      'command',
      true,
    );
    const previousSceneId = this.snapshot.scene.id;
    const { snapshot, reaction } = await this.source.choose(id);
    if (this.isDisposed()) return;
    this.snapshot = snapshot;
    const mood = animationForScene(snapshot.scene);
    if (reaction === undefined) this.stage.setMood(mood);
    else this.stage.playReaction(reaction, mood);
    this.stage.setEvidence(snapshot.evidence.count, snapshot.evidence.total);
    const resultText = snapshot.reaction?.[this.locale];
    if (resultText) {
      this.terminal.append(resultText, reaction === 'react-negative' ? 'bad' : 'ok');
      await this.terminal.whenIdle();
    }
    if (this.isDisposed()) return;
    await this.playSpeech(actionDialogue(id, this.locale));
    await this.presentScene(snapshot.scene.id !== previousSceneId || snapshot.isEnding);
  }

  update(deltaMs: number): void {
    this.stage.update(deltaMs);
    this.terminal.update(deltaMs);
  }

  destroy(): void {
    this.disposed = true;
    this.stage.destroy();
    this.terminal.destroy();
    this.view.destroy({ children: true });
  }
}
